import { useCallback, useId, useState } from "react";

export interface UseDisclosureProps {
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  id?: string;
}

type PropGetter = (props?: Record<string, any>) => Record<string, any>;

export interface UseDisclosureReturn {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onToggle: () => void;
  isControlled: boolean;
  getButtonProps: PropGetter;
  getDisclosureProps: PropGetter;
}

/**
 * Drop-in replacement for Chakra UI's `useDisclosure`, kept API-compatible so
 * consumers repoint by import path only. Supports controlled (`isOpen`) and
 * uncontrolled (`defaultIsOpen`) modes and exposes the `getButtonProps` /
 * `getDisclosureProps` accessibility prop-getters used by collapsible alerts.
 */
export const useDisclosure = (props: UseDisclosureProps = {}): UseDisclosureReturn => {
  const { onClose: onCloseProp, onOpen: onOpenProp, isOpen: isOpenProp, id: idProp } = props;

  const [isOpenState, setIsOpen] = useState(props.defaultIsOpen ?? false);

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? (isOpenProp as boolean) : isOpenState;

  const uid = useId();
  const id = idProp ?? `disclosure-${uid}`;

  const onClose = useCallback(() => {
    if (!isControlled) {
      setIsOpen(false);
    }
    onCloseProp?.();
  }, [isControlled, onCloseProp]);

  const onOpen = useCallback(() => {
    if (!isControlled) {
      setIsOpen(true);
    }
    onOpenProp?.();
  }, [isControlled, onOpenProp]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);

  const getButtonProps: PropGetter = useCallback(
    (buttonProps = {}) => ({
      ...buttonProps,
      "aria-expanded": isOpen,
      "aria-controls": id,
      onClick: (event: unknown) => {
        buttonProps.onClick?.(event);
        onToggle();
      },
    }),
    [isOpen, id, onToggle],
  );

  const getDisclosureProps: PropGetter = useCallback(
    (disclosureProps = {}) => ({
      ...disclosureProps,
      hidden: !isOpen,
      id,
    }),
    [isOpen, id],
  );

  return {
    isOpen,
    onOpen,
    onClose,
    onToggle,
    isControlled,
    getButtonProps,
    getDisclosureProps,
  };
};

export default useDisclosure;
